/* eslint-disable no-magic-numbers , complexity */
import React, { useEffect, useState } from 'react';
import get from 'lodash/get';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  getReportingReportsListUrl,
  matchAnyReportingViewReportUrl,
  matchOnboardingEditReportUrl
} from '@futrli/shared-services';
import { LiveReportDetail } from '@futrli-sdk/predict';
import { UILoader } from '@futrli/ui-components';

import { AuthStoreState } from '../../core/types';
import { getResourceDisplayState } from '../get-resource-display-mode';
import { OnboardingArrows } from '../components/onboarding/onboarding-arrows/onboarding-arrows';
import { ReportBuilder } from '../report-builder';
import { ReportBuilderContextProvider } from '../report-builder.context';
import { ReportView } from '../components/report-view/report-view';
import { usePredictionsInfoContext } from '../../forecast/detail/context/predictions-info-context';
import { useReportsContext } from '../reports.context';
import { useTemplatesContext } from '../templates.context';

interface Props {
  reportId: string;
}
export const ReportBuilderPage = ({ reportId }: Props) => {
  const intl = useIntl();
  const location = useLocation();
  const { resourceSharing } = useFlags();
  const { fetchReportById, patchReport, getPerformanceDashboards } = useReportsContext();
  const [report, setReport] = useState<LiveReportDetail | undefined>();
  const [reportNotFound, setReportNotFound] = useState(false);
  const [performanceDashboardsFetched, setPerformanceDashboardsFetched] = useState(false);
  const { organisationId: tempOrganisatioId, workspaceId: tempWorkspaceId } = useParams();
  const organisationId = tempOrganisatioId || '';
  const workspaceId = tempWorkspaceId || '';
  const { isFetchingTemplates } = useTemplatesContext();
  const { isConsolidation } = usePredictionsInfoContext();

  const isOnboarding = !!matchOnboardingEditReportUrl(location.pathname);
  const busy = isOnboarding ? isFetchingTemplates : false; // busy flag is to signal we are waiting for something else other than the report to load

  const currentUserId = useSelector((state: AuthStoreState) =>
    get(state, 'authReducers.authenticatedUser.attributes.sub', '')
  );
  const { displayMode: reportDisplayMode, shouldUseViewMode } = getResourceDisplayState(
    report,
    resourceSharing,
    currentUserId,
    !!matchAnyReportingViewReportUrl(location.pathname)
  );

  useEffect(() => {
    let isMounted = true;
    const getReport = async () => {
      try {
        const foundReport = await fetchReportById(reportId, { rethrowOnError: true });
        if (isMounted) {
          setReport(foundReport);
        }
      } catch {
        // the error toast is raised by the reports context, here we only bail out of the edit page
        if (isMounted) {
          setReportNotFound(true);
        }
      }
    };

    getReport();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  useEffect(() => {
    if (
      !shouldUseViewMode &&
      !performanceDashboardsFetched &&
      report?.orientation === 'dashboard' &&
      !isConsolidation
    ) {
      getPerformanceDashboards();
      setPerformanceDashboardsFetched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, performanceDashboardsFetched, shouldUseViewMode]);

  useEffect(() => {
    document.title = intl.formatMessage(
      { id: 'reporting.futrli_reporting_title', defaultMessage: `Futrli - Reporting - {reportName}` },
      {
        reportName:
          report?.name || intl.formatMessage({ id: 'reporting.untitled_report', defaultMessage: 'Untitled report' })
      }
    );
  }, [report, intl]);

  if (reportNotFound) {
    return <Navigate to={getReportingReportsListUrl(workspaceId, organisationId, isConsolidation)} replace />;
  }

  if (reportDisplayMode === 'no-access') {
    return <Navigate to={getReportingReportsListUrl(workspaceId, organisationId, isConsolidation)} replace />;
  }

  return report && !busy ? (
    <ReportBuilderContextProvider
      report={report}
      patchReport={patchReport}
      viewPage={shouldUseViewMode}
      key={report.id}
    >
      {shouldUseViewMode ? (
        <ReportView />
      ) : isOnboarding ? (
        <div style={{ position: 'relative' }}>
          <OnboardingArrows position="root" />
          <ReportBuilder />
        </div>
      ) : (
        <ReportBuilder />
      )}
    </ReportBuilderContextProvider>
  ) : (
    <UILoader isFull />
  );
};
